/**
 * Feature Flags Hook
 *
 * Provides access to team-level feature flags with safe defaults.
 * Flags default to enabled (true) if not set or during loading.
 */

import { useMemo } from 'react';
import { useTeamSettings } from './useTeamSettings';
import type { FeatureFlags } from './settings.schema';

/**
 * Default feature flags (all enabled)
 */
const DEFAULT_FLAGS: FeatureFlags = {
  quickCalculatorEnabled: true,
};

/**
 * Hook to access feature flags for the current team
 *
 * @param teamId - The team ID to get flags for
 * @returns Feature flags with safe defaults
 */
export function useFeatureFlags(teamId: string): {
  flags: FeatureFlags;
  loading: boolean;
} {
  const { settings, loading } = useTeamSettings(teamId);

  const flags = useMemo<FeatureFlags>(() => {
    // During loading or if features not set, use defaults
    if (!settings?.features) {
      return DEFAULT_FLAGS;
    }

    return {
      quickCalculatorEnabled: settings.features.quickCalculatorEnabled ?? true,
    };
  }, [settings?.features]);

  return { flags, loading };
}

/**
 * Check if quick calculator is enabled
 * Returns true during loading to avoid flash
 */
export function useQuickCalculatorEnabled(teamId: string): boolean {
  const { flags } = useFeatureFlags(teamId);
  return flags.quickCalculatorEnabled;
}
